/**
 * Root component: mounts the merged provider, fetches CCP pricing data up
 * front, and gates the wizard behind that fetch ("fetch first, then show the
 * experience").
 */
import { useEffect } from 'react';
import type { CSSProperties } from 'react';

import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { AllowanceStep } from './components/allowance-step';
import { EstimateStep } from './components/estimate-step';
import { NeedsStep } from './components/needs-step';
import { SummaryStep } from './components/summary-step';
import { UsageStep } from './components/usage-step';
import { UbpContextProvider, UbpContextProviderProps } from './context/context';
import { useSelector } from './context/hooks';
import { selectStep } from './context/selectors';
import { initialValuesFromQuery } from './context/url-helpers';

export type UsageBasedPricingCalculatorProps = Omit<
	UbpContextProviderProps,
	'children' | 'initialValues'
>;

export const UsageBasedPricingCalculator = (props: UsageBasedPricingCalculatorProps) => {
	const initialValues =
		typeof window !== 'undefined' ? initialValuesFromQuery(window.location.search) : undefined;
	return (
		<UbpContextProvider initialValues={initialValues} {...props}>
			<UsageBasedPricingCalculatorInner />
		</UbpContextProvider>
	);
};

const UsageBasedPricingCalculatorInner = () => {
	return (
		<div style={style}>
			<TypographyStyles>
				<CurrentStep />
			</TypographyStyles>
		</div>
	);
};

const CurrentStep = () => {
	const step = useSelector(selectStep);

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
	}, [step]);

	switch (step) {
		case 'usage':
			return <UsageStep />;
		case 'needs':
			return <NeedsStep />;
		case 'estimate':
			return <EstimateStep />;
		case 'allowance':
			return <AllowanceStep />;
		case 'summary':
			return <SummaryStep />;
	}
};

const style: CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	// Gap removed — each step owns its own vertical rhythm via paddings on
	// headings and section wrappers, so the design matches Figma exactly
	// without a wrapper-level gap fighting explicit values.
	gap: 0,
	// All padding + auto-margins removed for this page. Horizontal centering
	// is done by the parent flex container (stepLayoutStyles uses
	// alignItems: center), so this element stays full-width up to maxWidth
	// without introducing left/right auto margins.
	padding: 0,
	margin: 0,
	width: '100%',
	maxWidth: 1100,
	// No hardcoded font-family here — the child <TypographyStyles> wrapper
	// applies Charlie Display / Charlie Text at the root, and everything
	// below inherits. Previously this line forced the system-font stack
	// (`.SF NS` on macOS) which overrode the Charlie cascade and made
	// design-library components like <Tab> render in the wrong font.
};
